import { useRef, useEffect } from "react";
import { MinusOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { QUICK_PERCENTS } from "./itemDiscountMath";

const MIN_PERCENT = 0;
const MAX_PERCENT = 100;

/** „Без" + бързите проценти — сегментите под степера. */
const SEGMENT_PERCENTS = [0, ...QUICK_PERCENTS];

const clampPercent = (value: number): number =>
  Math.min(MAX_PERCENT, Math.max(MIN_PERCENT, Math.round(value)));

interface DiscountPercentPickerProps {
  percent: number;
  onChange: (percent: number) => void;
  /** На десктоп полето се отваря фокусирано и селектирано. */
  autoFocus: boolean;
  /** Избраните редове със собствен процент — общият не ги пипа. */
  ownPercentCount: number;
  isNarrow: boolean;
}

/** „Обща отстъпка" над списъка: заглавие + степер − [NN%] + вдясно, под тях
 *  сегменти Без/5…50%. Важи за избраните редове БЕЗ собствен процент. */
export const DiscountPercentPicker = ({
  percent,
  onChange,
  autoFocus,
  ownPercentCount,
  isNarrow,
}: DiscountPercentPickerProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!autoFocus) return;
    const timer = setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 100);
    return () => clearTimeout(timer);
  }, [autoFocus]);

  const handleInputChange = (raw: string) => {
    const digits = raw.replace(/[^0-9]/g, "");
    onChange(digits === "" ? 0 : clampPercent(Number(digits)));
  };

  const hint = (
    <p className="m-0 mt-1 text-[12.5px] font-medium leading-[1.35] text-pos-muted">
      {t("itemDiscounts.generalHint")}
    </p>
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[15px] font-bold leading-[1.2] text-pos-ink">
            {t("itemDiscounts.generalTitle")}
          </div>
          {/* На телефон подсказката е на цял ред под степера — до него се
              свива до 3–4 реда */}
          {!isNarrow && hint}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <StepButton
            icon={<MinusOutlined />}
            onClick={() => onChange(clampPercent(percent - 1))}
            disabled={percent <= MIN_PERCENT}
            isNarrow={isNarrow}
          />
          {/* Едно поле с „NN%" вътре — центрирано; пишат се само цифри */}
          <input
            ref={inputRef}
            value={`${percent}%`}
            onChange={(e) => handleInputChange(e.target.value)}
            onFocus={(e) => e.target.select()}
            inputMode="numeric"
            className={`rounded-xl border border-pos-accent bg-pos-card text-center font-pos-mono text-[22px] font-bold text-pos-accent-ink outline-none transition-colors focus:ring-[3px] focus:ring-pos-accent/15 ${
              isNarrow ? "h-10 w-[70px]" : "h-11 w-[84px]"
            }`}
          />
          <StepButton
            icon={<PlusOutlined />}
            onClick={() => onChange(clampPercent(percent + 1))}
            disabled={percent >= MAX_PERCENT}
            isNarrow={isNarrow}
          />
        </div>
      </div>
      {isNarrow && <div className="-mt-2">{hint}</div>}

      <div className="grid grid-cols-7 gap-1 rounded-xl bg-pos-line2 p-1">
        {SEGMENT_PERCENTS.map((value) => {
          const isActive = percent === value;
          return (
            <button
              key={value}
              type="button"
              onClick={() => onChange(value)}
              className={`h-9 cursor-pointer rounded-[9px] border-0 px-0 font-pos-mono text-[13px] font-bold transition-colors ${
                isActive
                  ? "bg-pos-card text-pos-accent-ink shadow-sm"
                  : "bg-transparent text-pos-soft2 hover:text-pos-ink"
              }`}
            >
              {value === 0 ? t("itemDiscounts.none") : `${value}%`}
            </button>
          );
        })}
      </div>

      {ownPercentCount > 0 && (
        <p className="m-0 text-[12px] font-medium text-pos-muted">
          {t("itemDiscounts.ownPercentCount", { count: ownPercentCount })}
        </p>
      )}
    </div>
  );
};

const StepButton = ({
  icon,
  onClick,
  disabled,
  isNarrow,
}: {
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  isNarrow: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className={`inline-flex cursor-pointer items-center justify-center rounded-[10px] border border-pos-line bg-pos-card text-sm text-pos-soft2 transition-colors disabled:cursor-not-allowed disabled:opacity-40 enabled:hover:border-pos-accent/30 enabled:hover:bg-pos-marked enabled:hover:text-pos-accent-ink ${
      isNarrow ? "h-10 w-10" : "h-11 w-11"
    }`}
  >
    {icon}
  </button>
);
